import { get as serverGet } from '../server';
import { urls as locationsUrls } from 'Reducers/locations';
import * as urlHelper from 'Helpers/url';
import { popularLocationsParamsSelectorWithParam } from 'Selectors/popularLocations';
import { getLocationsFromGPSSelector } from 'Selectors/location';
import { getGeolocationPosition } from 'Actions/categoryBrowsing';
import { UPDATE_GPS_DATA } from 'Constants/locations';
import { getLocationPathByLatLon } from './locationPath';
import { getType } from 'Reducers/helpers/typeModifier';

const getLocationUrl = (state, url, params = {}) => {
    if (state.api && state.api.queryParam && state.api.queryParam.enableMultiLang) {
        params.lang = state.api.queryParam.lang;
    }

    const strParams = urlHelper.paramsToQueryString(params);

    return strParams ? `${url}?${strParams}` : url;
};

const shouldFetchLocation = (state, params, id) => {
    const { locations: { collections, isFetching, isError }} = state;
    const url = id
        ? `${locationsUrls.getLocation(id)}`
        : getLocationUrl(state, locationsUrls.getLocations, params);
    const type = getType('LOCATIONS', url);
    const prevData = collections[type];
    const isFetchingData = isFetching[type];
    const hasError = isError[type];

    return !prevData && !isFetchingData && !hasError;
};

const getLocationsById = (id, defaultCity, countryId, headers = {}) => (dispatch, getState) => {
    const state = getState();
    const shouldFetch = shouldFetchLocation(state, undefined, id);

    if (shouldFetch) {
        return dispatch(serverGet(locationsUrls.getLocation(id), 'LOCATIONS', { defaultCity, countryId }, headers));
    }
    return Promise.resolve();
};

const getLocations = (params = {}, baseUrl = '', headers = {}) => (dispatch, getState) => {
    const state = getState();
    const shouldFetch = shouldFetchLocation(state, { ...params });

    if (shouldFetch) {
        return dispatch(serverGet(`${baseUrl}${locationsUrls.getLocations}`, 'LOCATIONS', params, headers));
    }
    return Promise.resolve();
};

const getCountryLocation = (params = {}, baseUrl = '', headers = {}) => dispatch =>
    dispatch(
        serverGet(
            `${baseUrl}${locationsUrls.getLocations}`,
            'LOCATIONS',
            { ...params, levels: 'country' },
            headers
        )
    );

const getSuggestions = (params = {}) => dispatch =>
    dispatch(serverGet(locationsUrls.getAutocomplete, 'AUTOCOMPLETE_LOCATIONS', params));

const getPopularLocations = config => (dispatch, getState, { get }) => {
    const state = getState();
    const { locations } = state;
    const { selectedLocation, gps } = locations;
    const popularParams = gps && gps.currentLocation
        ? getLocationsFromGPSSelector(state)
        : selectedLocation;
    const isCountryWithoutState = config && config.get('isCountryWithoutState');
    const params = popularLocationsParamsSelectorWithParam(state, popularParams, isCountryWithoutState);
    const url = getLocationUrl(state, locationsUrls.getPopulars, params);
    const type = getType('LOCATIONS', url);
    const isFetchingPopular = locations.isFetching[type];
    const prevData = locations.collections[type];
    const hasError = locations.isError[type];

    if (!isFetchingPopular && !prevData && !hasError) {
        return dispatch(get(locationsUrls.getPopulars, 'LOCATIONS', params));
    }
    return Promise.resolve();
};

const receiveGPS = data => ({ type: 'RECEIVE_GPS_LOCATION', data });

const requestGps = () => ({ type: 'REQUEST_GPS_LOCATION' });

const errorGps = () => ({ type: 'ERROR_GPS_LOCATION' });

const setLocationForGps = data => ({ type: 'SET_LOCATION_GPS', data });

/**
 * Sets the given location object as the 'selectedLocation' in redux store
 * But removes the 'gps' property from the provided object before doing so
 * @param {*} data - The location object
 */
const setLocationGps = data => dispatch => {
    const filterGps = Object.assign({}, data);

    if (filterGps.gps) {
        delete filterGps.gps;
    }
    return dispatch(setLocationForGps(filterGps));
};

/**
 * Sets the given gps object into the gps property of the redux store
 * if lat and long are present in it
 * @param {*} gps the gps object of an location object
 */
const setGPS = gps => dispatch => {
    if (gps && gps.latitude && gps.longitude) {
        return dispatch(receiveGPS(gps));
    }
    return dispatch(receiveGPS());
};

const updateGPSStatus = () => (dispatch, getState) => {
    dispatch(requestGps());

    return getGeolocationPosition().then(response => {
        return getLocationPathByLatLon({ lat: response.latitude, lon: response.longitude })(dispatch, getState);
    }).then(({ data: { data = {}} = {}}) => {
        dispatch({
            type: UPDATE_GPS_DATA,
            data: {
                status: 'granted',
                location: data,
                isFetching: false
            }
        });
    }).catch(() => dispatch({
        type: UPDATE_GPS_DATA,
        data: {
            status: 'rejected',
            location: {},
            isFetching: false
        }
    }));
};

export {
    getLocationsById,
    getLocations,
    getSuggestions,
    getPopularLocations,
    shouldFetchLocation,
    getCountryLocation,
    setGPS,
    requestGps,
    errorGps,
    setLocationGps,
    updateGPSStatus
};
